import type { CreateContextReturn } from 'common/ui/util/createContext';
import { createContext } from 'common/ui/util/createContext';
import type { DreAllViewportTypes, ViewportTypeKeys } from 'config/constants';
import { DRE_ALL_2D_VIEWPORT_TYPES, DRE_ALL_VIEWPORT_TYPES } from 'config/constants';
import { ScrollLinkMode, scrollLinkModeAtom } from 'domains/viewer/ViewportDre/modules/state';
import {
  getOffscreenPriority,
  getUnhungPriority,
  LOADING_PRIORITIES,
} from 'domains/viewer/loadingPriorities';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useUserAnalytics } from 'hooks/useCurrentUser/legacy';
import { useViewerId } from 'hooks/useViewerId';
import { useDatadogSessionId } from 'modules/analytics/datadog';
import { FF, useFeatureFlagEnabled, useSplitFlag } from 'modules/feature-flags/suspense';
import { clearProviderMap } from 'modules/viewer/workers/PixelDataSharedWorker';
import type { PixelDataSharedWorker } from 'modules/viewer/workers/PixelDataSharedWorker';
import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import usePreviousDistinct from 'react-use/esm/usePreviousDistinct';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { SUPPORTED_TEXTURES } from 'utils/textureUtils';
import { useInitStepBarPlacement } from '../HangingProtocol/hooks/useInitStepBarPlacement';
import {
  getStepsFromVersioned,
  hangingProtocolStepIndex,
  steppedHPConfigurations,
} from '../HangingProtocol/state';
import { isPhase4Steps } from './StudiesBar/HangingProtocols/constants';
import { openedViewersState } from '../TrackOpenedViewers';
import {
  type BaseImagingProvider,
  ImagingProviderLoadType,
} from '../ViewportDre/modules/imaging/BaseImagingProvider';
import LinkManager from '../ViewportDre/modules/imaging/LinkManager';
import { MultiStackImagingProvider } from '../ViewportDre/modules/imaging/MultiStackImagingProvider';
import { RenderManager } from '../ViewportDre/modules/imaging/RenderManager';
import { SynchronizationBroadcaster } from '../ViewportDre/modules/imaging/SynchronizationBroadcaster';
import {
  isWorkerLoadedImagingProvider,
  WorkerLoadedImagingProvider,
} from '../ViewportDre/modules/imaging/WorkerLoadedImagingProvider';
import { collectProvidersInWindowViewports } from './viewportProviders';
import { getUpcomingStepDistances } from './stepLoadPriorities';
import { getInitialSlice, InferiorToSuperiorCheck } from '../ViewportDre/modules/imaging/utils';
import {
  logViewportLoadStart,
  useViewportMetrics,
} from '../ViewportDre/modules/useMeasureViewerPerformance';
import { viewportDisplayConfigurationSelector } from '../ViewportsConfigurations';
import { rehydrateSlimStackToLayeredStack } from '../ViewportsConfigurations/manipulators';
import {
  useGuideViewportConfigurationSelector,
  useViewportsConfigurationsSelector,
} from '../ViewportsConfigurations/state';
import { useViewerContextBroadcaster } from '../ViewportsConfigurations/useViewerContextBroadcaster';
import type {
  CompositeStackConfig,
  FullMultiLayerStack,
  FullSingleLayerStack,
  Series,
  SeriesMatch,
  Stack,
  Study,
  ViewportsConfigurations,
} from '../ViewportsConfigurations/types';
import { useRenderedViewportIdsForWindow } from '../hooks/useRenderedViewportIds';
import { useViewerDownloadingFlags } from '../hooks/useViewerDownloadingFlags';
import { useOpenPixelDataSharedWorker } from './useOpenPixelDataSharedWorker';
import { useReinitializeProvidersOnWorkerChange } from './useReinitializeProvidersOnWorkerChange';
import { useBaseViewerData } from './StudyLoader/useStudies';
import { ViewerMemoryManager } from './ViewerMemoryManager';
import { getHybridMammographySwapProvidersInWindow } from './useViewerMemoryConfig';
import {
  encodeCompositeStackSmid,
  isStackSmidComposite,
  decodeCompositeStackSmid,
  decodeOverlayStackSmidToStackLayers,
  isStackSmidMultiLayer,
} from './stackUtils';
import { ViewerRecoilContext } from './viewerRecoilContext';
import { viewportIdToConfig } from './viewerUtils';

import { useStartingImageSetting } from 'hooks/useViewerSettings';
import { telemetryContextFor } from 'modules/Apollo/types';
import {
  buildHybridStackConfig,
  buildPriorSeriesConfig,
} from '../ViewportDre/modules/imaging/classificationBasedMatching';
import { MultiObjectImagingProvider } from '../ViewportDre/modules/imaging/MultiObjectImagingProvider';
import { BaseSingleStackImagingProvider } from '../ViewportDre/modules/imaging/BaseSingleStackImagingProvider';
import { DreDebugQueryParam } from '../ViewportDre/constants';
import { useSearchParams } from 'react-router-dom';

type SliceChangeMessageDetails = {
  viewType: DreAllViewportTypes;
  viewportId: string;
  stackSmid: string;
  slice: number;
  frameSmid: string;
};

export type SliceChangeMessage = {
  action: 'sliceChange';
  details: SliceChangeMessageDetails;
};

export type StackSyncBroadcastChannelMessage = SliceChangeMessage;

export type TStackProviders = Readonly<Map<string, BaseImagingProvider<Stack>>>;
export type TViewerContext = {
  stackProviders: TStackProviders;
  hybridMammographyEnabled: boolean;
  linkManager: LinkManager;
  synchronizationBroadcaster: SynchronizationBroadcaster;
  syncAllSnapshots: () => void;
  pixelDataSharedWorker: Nullable<PixelDataSharedWorker>;
};

/**
 * Type guard to check if a provider is a pixel provider
 */
export const isPixelProvider = (
  provider:
    | BaseImagingProvider<Stack>
    | MultiStackImagingProvider<FullMultiLayerStack>
    | WorkerLoadedImagingProvider<FullSingleLayerStack>
    | MultiObjectImagingProvider
): provider is
  | MultiStackImagingProvider<FullMultiLayerStack>
  | WorkerLoadedImagingProvider<FullSingleLayerStack>
  | MultiObjectImagingProvider => provider.canRender('vtk') && provider.hasPixels();

const [ContextProvider, useViewerContext]: CreateContextReturn<TViewerContext> =
  createContext<TViewerContext>({
    strict: true,
    errorMessage:
      'useViewerContext: `context` is undefined. Seems you forgot to wrap component within the ViewerContextProvider',
    name: 'ViewerContext',
  });

export const findSeriesForStack = (study: Study, stack: Stack): Series | undefined => {
  return study.seriesList.find(
    (s) => 'frames' in stack && stack.frames.length > 0 && s.smid === stack.frames[0].series.smid
  );
};

export const linkCompositeProviders = (providers: Map<string, BaseImagingProvider<Stack>>) => {
  for (const provider of providers.values()) {
    if (provider instanceof MultiObjectImagingProvider) {
      provider.calculateLinkedStacks(providers);
    }
  }
};

export const getBaseSingleStackProvider = (
  providers: Map<string, BaseImagingProvider<Stack>>,
  smid: string
): BaseSingleStackImagingProvider | null => {
  const provider = providers.get(smid);
  if (provider != null && provider instanceof BaseSingleStackImagingProvider) {
    return provider;
  }
  return null;
};

export const findSingleLayerStack = (study: Study, smid: string): FullSingleLayerStack | null => {
  const stack = study.stackedFrames.find((s) => s.smid === smid);
  if (stack != null && stack.__typename === 'SingleLayerStack') {
    return stack as FullSingleLayerStack;
  }
  return null;
};

/**
 * Reconstructs SeriesMatch[] from previously-encoded composite stack SMIDs.
 * Used when rehydrating composite providers from viewport configurations.
 */
export const buildMatchesFromDecodedSmids = (
  matchedSmids: readonly string[],
  providers: Map<string, BaseImagingProvider<Stack>>,
  studies: Study[],
  topStudySmid: string
): SeriesMatch[] => {
  return matchedSmids
    .map((matchedSmid) => {
      const matchedProvider = getBaseSingleStackProvider(providers, matchedSmid);
      if (matchedProvider == null) return null;

      const matchedStudy = studies.find((s) => s.smid === matchedProvider.study.smid);
      if (matchedStudy == null) return null;

      const matchedStack = findSingleLayerStack(matchedStudy, matchedSmid);
      if (matchedStack == null) return null;

      return {
        stack: matchedStack,
        study: matchedStudy,
        studyDate: 'studyDate' in matchedStudy ? (matchedStudy.studyDate as string | null) : null,
        seriesDescription: null,
        matchType: matchedStudy.smid === topStudySmid ? ('hybrid' as const) : ('prior' as const),
      };
    })
    .filter((m): m is NonNullable<typeof m> => m != null);
};

/**
 * Creates a composite provider from a viewport configuration that has a composite stack SMID.
 * Decodes the SMID, looks up the top provider/study/stack, builds matches, and calls createProviderFn.
 * Returns null if any piece is missing.
 */
export const createCompositeFromViewportConfig = ({
  compositeSmid,
  providers,
  studies,
  createProviderFn,
}: {
  compositeSmid: string;
  providers: Map<string, BaseImagingProvider<Stack>>;
  studies: Study[];
  createProviderFn: (params: {
    compositeStackSmid: string;
    compositeConfig: CompositeStackConfig;
    topProvider: BaseSingleStackImagingProvider;
    topStudy: Study;
    topSeries: Nullable<Series>;
    providerMap: Map<string, BaseImagingProvider<Stack>>;
  }) => MultiObjectImagingProvider;
}): MultiObjectImagingProvider | null => {
  const decoded = decodeCompositeStackSmid(compositeSmid);
  if (decoded == null) return null;

  const { topSmid, matchedSmids } = decoded;

  const topProvider = getBaseSingleStackProvider(providers, topSmid);
  if (topProvider == null) return null;

  const topStudy = studies.find((s) => s.smid === topProvider.study.smid);
  if (topStudy == null) return null;

  const topStack = findSingleLayerStack(topStudy, topSmid);
  if (topStack == null) return null;

  const matches = buildMatchesFromDecodedSmids(matchedSmids, providers, studies, topStudy.smid);
  if (matches.length === 0) return null;

  const compositeConfig: CompositeStackConfig = {
    topStack,
    topStudy,
    topSeriesDescription: null,
    matches,
  };

  const topSeries = findSeriesForStack(topStudy, topStack);

  return createProviderFn({
    compositeStackSmid: compositeSmid,
    compositeConfig,
    topProvider,
    topStudy,
    topSeries,
    providerMap: providers,
  });
};

/**
 * Checks whether all constituent stacks of a composite SMID still exist in the given studies.
 */

export const areAllCompositeStacksPresent = (stackSmid: string, studies: Study[]): boolean => {
  const decoded = decodeCompositeStackSmid(stackSmid);
  if (decoded == null) return false;
  const allSmids = [decoded.topSmid, ...decoded.matchedSmids];
  return allSmids.every((smid) =>
    studies.some((study) => study.stackedFrames.some((stack) => stack.smid === smid))
  );
};

/**
 * Scans viewport configurations and finds stacks that can be composed into composite stacks.
 * Returns an array of composite configs with matching metadata.
 */

export const scanViewportsForCompositeConfigs = ({
  viewportsConfigurations,
  stackProviders,
  studies,
  mainStudiesIds,
  enableHybridStacks,
  enablePriorStacks,
}: {
  viewportsConfigurations: Record<string, any>;
  stackProviders: Map<string, BaseImagingProvider<Stack>>;
  studies: readonly Study[];
  mainStudiesIds: readonly string[];
  enableHybridStacks: boolean;
  enablePriorStacks: boolean;
}): Array<{
  viewportId: string;
  config: CompositeStackConfig;
  topProvider: BaseSingleStackImagingProvider;
  topSeries: Nullable<Series>;
  topStudy: Study;
}> => {
  const processedStackSmids = new Set<string>();
  const compositeConfigs: Array<{
    viewportId: string;
    config: CompositeStackConfig;
    topProvider: BaseSingleStackImagingProvider;
    topSeries: Nullable<Series>;
    topStudy: Study;
  }> = [];

  for (const [viewportId, vpConfig] of Object.entries(viewportsConfigurations)) {
    if (vpConfig?.stack?.smid == null || vpConfig?.study?.smid == null) {
      continue;
    }

    const stackSmid = vpConfig.stack.smid;

    // Skip if already processed, already a composite, or already a multi-layer
    if (
      processedStackSmids.has(stackSmid) ||
      isStackSmidComposite(stackSmid) ||
      isStackSmidMultiLayer(stackSmid)
    ) {
      continue;
    }
    processedStackSmids.add(stackSmid);

    const topProvider = getBaseSingleStackProvider(stackProviders, stackSmid);
    if (topProvider == null) continue;

    const study = studies.find((s) => s.smid === vpConfig.study?.smid);
    if (study == null) continue;

    const stack = findSingleLayerStack(study, stackSmid);
    if (stack == null) continue;

    // Build composite config based on which mode is enabled
    const compositeConfig = enableHybridStacks
      ? buildHybridStackConfig(stack, study)
      : enablePriorStacks
        ? buildPriorSeriesConfig(stack, study, studies, mainStudiesIds)
        : null;

    if (compositeConfig != null && compositeConfig.matches.length > 0) {
      const topSeries = findSeriesForStack(study, stack);

      compositeConfigs.push({
        viewportId,
        config: compositeConfig,
        topProvider,
        topSeries,
        topStudy: study,
      });
    }
  }

  return compositeConfigs;
};

/**
 * VX-4775: whether an initially-unhanged stack should be prefetched to the on-disk cache only, rather
 * than transferred into renderer memory up front. Reads the `VIEWER_RESTRICT_INITIAL_LOADS` treatment:
 *
 * - `off` — today's behaviour: load everything the memory config allows into memory up front.
 * - `all` — only what is hanged goes into memory; every other stack waits for its first drop.
 * - `priors` — the current exam loads into memory as normal; unhanged prior stacks wait. Radiologists
 *   are generally expected to look at every series in the current exam, so restricting those would be
 *   felt; expectations are looser for priors, which are also usually the bulk of the unhanged data.
 *
 * Any unrecognized treatment falls through to no restriction, so a bad flag value degrades to today's
 * behaviour rather than to withheld pixels.
 */
export function shouldRestrictInitialLoad({
  restrictInitialLoads,
  studySmid,
  mainStudySmids,
}: {
  restrictInitialLoads: string;
  studySmid: string;
  mainStudySmids: readonly string[] | undefined;
}): boolean {
  if (restrictInitialLoads === 'all') return true;
  if (restrictInitialLoads !== 'priors') return false;
  // Without the current-exam list there is no way to tell a prior from the current exam, so leave the
  // stack unrestricted rather than guess — same fail-safe direction as an unrecognized treatment.
  if (mainStudySmids == null || mainStudySmids.length === 0) return false;
  return !mainStudySmids.includes(studySmid);
}

const InnerProvider = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  // Get viewerId first since it's needed for multiple operations
  const viewerId = useViewerId();

  // Broadcast viewer context to other windows (e.g., AI Sandbox)
  // Only enable for Viewer 0 to prevent duplicate broadcasts from multiple viewer windows
  useViewerContextBroadcaster({ context: APOLLO_QUERY_CONTEXT, enabled: viewerId === '0' });

  const { studies, mainStudiesIds } = useBaseViewerData({
    context: APOLLO_QUERY_CONTEXT,
    shouldEmitPrecacheStatus: true,
  });
  const caseSmid = useCurrentCaseId();
  const scrollLinkMode = useRecoilValue(scrollLinkModeAtom(caseSmid));
  const localizeMultiAxes = useFeatureFlagEnabled(FF.LOCALIZE_MULTI_AXES);
  const multiObjectStacksEnabled = useFeatureFlagEnabled(FF.MULTI_OBJECT_STACKS);
  const restrictInitialLoads = useSplitFlag(FF.VIEWER_RESTRICT_INITIAL_LOADS);
  const stepAwareLoadPriority = useSplitFlag(FF.VIEWER_STEP_AWARE_LOAD_PRIORITY);
  const hangingProtocolPhase = useSplitFlag(FF.HANGING_PROTOCOL_PHASE);
  const isStepAwareLoadPriorityEnabled =
    stepAwareLoadPriority === 'on' && isPhase4Steps(hangingProtocolPhase);
  // Both are broadcast, so every viewer window ranks the steps the same way.
  const steps = getStepsFromVersioned(useRecoilValue(steppedHPConfigurations));
  const stepIndex = useRecoilValue(hangingProtocolStepIndex);
  const [searchParams] = useSearchParams();
  const linkManagerRef = useRef<LinkManager>(new LinkManager({ localizeMultiAxes }));

  const synchronizationBroadcaster = useRef(new SynchronizationBroadcaster());
  const renderManagerRef = useRef(new RenderManager());
  const guideViewportConfigurationSelector = useGuideViewportConfigurationSelector({
    context: APOLLO_QUERY_CONTEXT,
  });
  const guideViewportConfiguration = useRecoilValue(guideViewportConfigurationSelector);
  const [viewerDownloadingFlags, loadingViewerDownloadingFlags] = useViewerDownloadingFlags();

  const userAnalytics = useUserAnalytics({ context: APOLLO_QUERY_CONTEXT });
  const datadogSessionId = useDatadogSessionId();
  // Use caseSmid as the sharedWorkerViewId for Datadog view tracking
  const sharedWorkerViewId = caseSmid;

  // for type safety, we add `?? ''`, but this code only gets reached once a case is available
  // (ViewerRecoilContext doesn't render children until viewport configurations are defined for a case)
  const workerKey = caseSmid ?? '';
  const pixelDataSharedWorker = useOpenPixelDataSharedWorker(workerKey);

  const [startingImageSetting] = useStartingImageSetting({ context: APOLLO_QUERY_CONTEXT });

  const viewportsConfigurationsSelector = useViewportsConfigurationsSelector({
    context: APOLLO_QUERY_CONTEXT,
  });

  const createImagingProvider = useRecoilCallback(
    ({ snapshot }) =>
      (
        viewportsConfigurations: ViewportsConfigurations,
        study: Study,
        stack: Stack,
        series: Nullable<Series>,
        pixelDataSharedWorker: PixelDataSharedWorker
      ) => {
        let initialLoad = false;
        const supportedTextures = SUPPORTED_TEXTURES;

        const getProvider = () => {
          if (stack.__typename === 'LayeredStack') {
            const providerParams = {
              study,
              stack,
              singleSortedSeries: true,
              linkManager: linkManagerRef.current,
              synchronizationBroadcaster: synchronizationBroadcaster.current,
              renderManager: renderManagerRef.current,
              frameOfReferenceUID: series?.frameOfReferenceUID,
              series,
              imageRegistrations: [],
              supportedTextures,
            } as const;
            return new MultiStackImagingProvider<FullMultiLayerStack>(providerParams);
          } else if (stack.__typename === 'SingleLayerStack') {
            const providerParams = {
              study,
              stack,
              singleSortedSeries: true,
              linkManager: linkManagerRef.current,
              synchronizationBroadcaster: synchronizationBroadcaster.current,
              renderManager: renderManagerRef.current,
              frameOfReferenceUID: series?.frameOfReferenceUID,
              series,
              pixelDataSharedWorker,
              imageRegistrations: study.seriesList
                .filter((series) => stack.frames.some((frame) => frame.series.smid === series.smid))
                .flatMap((series) => series.imageRegistrations),
              supportedTextures,
              workerInitializationOptions: {
                ...viewerDownloadingFlags,
                userAnalytics,
                datadogSessionId,
                sharedWorkerViewId,
              },
            } as const;
            return new WorkerLoadedImagingProvider<FullSingleLayerStack>(providerParams);
          }
        };
        const provider = getProvider();

        if (provider == null) {
          throw new Error('No provider generated for stack');
        }
        if (viewportsConfigurations != null && isPixelProvider(provider)) {
          for (const [viewportId, viewportConfig] of Object.entries(viewportsConfigurations)) {
            if (viewportConfig == null || viewportConfig.stack == null) continue;

            if (
              viewportConfig.stack.smid === stack.smid &&
              viewportIdToConfig(viewportId).windowId === viewerId
            ) {
              const viewType = viewportConfig.viewType as DreAllViewportTypes;
              const viewportDisplayKey = provider.getViewportDisplayConfigKey({
                viewType,
                viewportId,
              });

              const viewportDisplayConfig = snapshot
                .getLoadable(viewportDisplayConfigurationSelector(viewportDisplayKey))
                .getValue();

              const initialSlice = provider.isLayeredStack()
                ? (viewportDisplayConfig?.params2d?.imageNumber ?? 0)
                : (viewportDisplayConfig?.params2d?.imageNumber ??
                  getInitialSlice(
                    provider.vtkImage,
                    provider.inferiorToSuperiorCheck === InferiorToSuperiorCheck.InferiorToSuperior,
                    startingImageSetting,
                    viewportDisplayConfig
                  ));

              provider._updateVtkSlice(viewType, viewportId, initialSlice, false);

              logViewportLoadStart(viewportId);
              initialLoad = true;

              // set this value before loading, so the analytics engine knows how to set up this load
              provider.setWasPartOfInitialLoad(initialLoad);

              provider.load({
                initialSlice,
                priority: LOADING_PRIORITIES.HANGED,
              });
            }
          }
        }

        provider.setWasPartOfInitialLoad(initialLoad);

        return provider;
      },
    [
      viewerId,
      viewerDownloadingFlags,
      userAnalytics,
      datadogSessionId,
      startingImageSetting,
      sharedWorkerViewId,
    ]
  );

  const createCompositeProvider = useCallback(
    ({
      compositeStackSmid,
      compositeConfig,
      topProvider,
      topStudy,
      topSeries,
      providerMap,
    }: {
      compositeStackSmid: string;
      compositeConfig: CompositeStackConfig;
      topProvider: BaseSingleStackImagingProvider;
      topStudy: Study;
      topSeries: Nullable<Series>;
      providerMap: Map<string, BaseImagingProvider<Stack>>;
    }): MultiObjectImagingProvider => {
      const compositeProvider = new MultiObjectImagingProvider({
        compositeStackSmid,
        config: compositeConfig,
        topProvider,
        study: topStudy,
        singleSortedSeries: true,
        linkManager: linkManagerRef.current,
        synchronizationBroadcaster: synchronizationBroadcaster.current,
        renderManager: renderManagerRef.current,
        frameOfReferenceUID: topSeries?.frameOfReferenceUID,
        series: topSeries ?? null,
        imageRegistrations: [],
        supportedTextures: SUPPORTED_TEXTURES,
      });
      compositeProvider.calculateConstituentProviders(providerMap);
      return compositeProvider;
    },
    []
  );

  const windowId = viewerId;
  const windows = useRecoilValue(openedViewersState);
  const viewportsToRender = useRenderedViewportIdsForWindow({
    windowId: windowId ?? '',
    context: APOLLO_QUERY_CONTEXT,
  });
  useViewportMetrics(viewportsToRender);
  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const me = data?.me;

  useInitStepBarPlacement(me?.viewerSettings?.stepBarPlacement);

  const viewportsConfigurations = useRecoilValue(viewportsConfigurationsSelector);
  const previousViewportsConfigurations = usePreviousDistinct(viewportsConfigurations);
  const [stackProviders, setStackProviders] = useState<TStackProviders>(new Map());

  // VX-4223: when the watchdog recreates the shared worker, rebind existing
  // providers to the new port and re-issue their in-flight loads so the page
  // self-heals without a manual reload.
  useReinitializeProvidersOnWorkerChange(pixelDataSharedWorker, stackProviders);

  const areViewportsReady =
    (viewportsConfigurations != null
      ? Object.values(viewportsConfigurations).some((c) => c != null)
      : false) &&
    loadingViewerDownloadingFlags === false &&
    // @ts-expect-error [EN-10263] - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'.
    windows.includes(windowId);

  const generateProviders = useCallback(
    (
      viewportsConfigurations: ViewportsConfigurations,
      studiesWithStackedFrames: Study[],
      pixelDataSharedWorker: PixelDataSharedWorker,
      stackProviders: TStackProviders
    ): {
      providers: TStackProviders;
      hasUpdatedProviders: boolean;
    } => {
      const filteredProviders = new Map(
        [...stackProviders].filter(
          // @ts-expect-error [EN-7967] - TS2769 - No overload matches this call.
          ([stackSmid]: [any]) =>
            // Keep multi-layer (fusion) stacks if all constituent layers still exist
            (isStackSmidMultiLayer(stackSmid) &&
              decodeOverlayStackSmidToStackLayers(stackSmid).every((layer) => {
                return studiesWithStackedFrames.some((study) =>
                  study.stackedFrames.some((stack) => stack.smid === layer.stackSmid)
                );
              })) ||
            // Keep composite stacks if all constituent stacks still exist
            (isStackSmidComposite(stackSmid) &&
              areAllCompositeStacksPresent(stackSmid, studiesWithStackedFrames)) ||
            // Keep regular stacks
            studiesWithStackedFrames.some((study) =>
              study.stackedFrames.some((stack) => stack.smid === stackSmid)
            )
        )
      );

      for (const [smid, provider] of stackProviders) {
        if (!filteredProviders.has(smid)) {
          provider.dispose();
        }
      }

      let hasUpdatedProviders = filteredProviders.size !== stackProviders.size;

      const providers = studiesWithStackedFrames.reduce(
        (map: Map<string, BaseImagingProvider<Stack>>, study: Study) => {
          const writableMap = new Map<string, BaseImagingProvider<Stack>>(map);
          study.stackedFrames.forEach((stack) => {
            // TODO: Figure out how to handle series level data once we have stacks
            //       containing multiple distinct series.
            const series = study.seriesList.find(
              // @ts-expect-error [EN-7967] - TS2339 - Property 'frames' does not exist on type '{ readonly __typename?: "LayeredStack"; readonly smid: string; readonly type: string; readonly study: { readonly __typename?: "Study"; readonly smid: string; }; readonly stackLayers: readonly { readonly __typename?: "Layer"; readonly stackSmid: string; readonly index: number; }[]; } | { ...; }'. | TS2339 - Property 'frames' does not exist on type '{ readonly __typename?: "LayeredStack"; readonly smid: string; readonly type: string; readonly study: { readonly __typename?: "Study"; readonly smid: string; }; readonly stackLayers: readonly { readonly __typename?: "Layer"; readonly stackSmid: string; readonly index: number; }[]; } | { ...; }'.
              (s) => stack.frames != null && s.smid === stack.frames[0].series.smid
            );

            if (!map.has(stack.smid)) {
              const imagingProvider = createImagingProvider(
                viewportsConfigurations,
                study,
                stack,
                series,
                pixelDataSharedWorker
              );

              hasUpdatedProviders = true;
              linkManagerRef.current.purgeOldProviderLinks(writableMap);
              imagingProvider.calculateLinkedStacks(writableMap);
              writableMap.set(stack.smid, imagingProvider);
            }
          });

          return writableMap;
        },
        filteredProviders
      );

      Object.values(viewportsConfigurations).forEach((config) => {
        if (
          config?.stack != null &&
          config.stack.smid != null &&
          isStackSmidMultiLayer(config?.stack.smid)
        ) {
          const study = studiesWithStackedFrames.find((s) => s.smid === config?.study?.smid);

          const stack = rehydrateSlimStackToLayeredStack(config.stack, study);
          if (study != null && stack != null && !providers.has(stack.smid)) {
            const imagingProvider = createImagingProvider(
              viewportsConfigurations,
              study,
              stack,
              null,
              pixelDataSharedWorker
            );

            hasUpdatedProviders = true;
            imagingProvider.calculateLayerProviders(providers);
            imagingProvider.calculateLinkedStacks(providers);
            providers.set(stack.smid, imagingProvider);
          }
        }

        // Handle composite stacks (concatenated matched stacks)
        if (
          config?.stack != null &&
          config.stack.smid != null &&
          isStackSmidComposite(config.stack.smid) &&
          !providers.has(config.stack.smid)
        ) {
          const compositeProvider = createCompositeFromViewportConfig({
            compositeSmid: config.stack.smid,
            providers,
            studies: studiesWithStackedFrames,
            createProviderFn: createCompositeProvider,
          });

          if (compositeProvider != null) {
            hasUpdatedProviders = true;
            providers.set(config.stack.smid, compositeProvider);
          }
        }
      });

      // Determine which map will be returned
      const finalProviders = hasUpdatedProviders ? providers : stackProviders;

      linkManagerRef.current.purgeOldProviderLinks(finalProviders);

      // Ensure each composite provider has references to all other providers
      linkCompositeProviders(finalProviders);

      return { providers: finalProviders, hasUpdatedProviders };
    },
    [createImagingProvider, createCompositeProvider]
  );

  useEffect(() => {
    stackProviders.forEach((provider) => {
      provider.updateAbsoluteScrollPreference(me?.viewerSettings?.absoluteScroll ?? true);
    });
  }, [me, stackProviders]);

  useEffect(() => {
    if (viewportsConfigurations == null || !areViewportsReady || pixelDataSharedWorker == null)
      return;
    setStackProviders((previousProviders) => {
      const { providers, hasUpdatedProviders } = generateProviders(
        viewportsConfigurations,
        [...studies],
        pixelDataSharedWorker,
        previousProviders
      );
      if (hasUpdatedProviders) {
        return providers;
      } else {
        return previousProviders;
      }
    });
  }, [
    viewportsConfigurations,
    generateProviders,
    studies,
    areViewportsReady,
    pixelDataSharedWorker,
  ]);

  const enablePriorStacks = searchParams.get(DreDebugQueryParam.EnablePriorStacks) === 'true';
  const hybridMammographyEnabled = multiObjectStacksEnabled;

  useEffect(() => {
    if (
      !multiObjectStacksEnabled ||
      !enablePriorStacks ||
      viewportsConfigurations == null ||
      studies.length === 0 ||
      mainStudiesIds.length === 0 ||
      !areViewportsReady ||
      pixelDataSharedWorker == null
    ) {
      return;
    }

    const compositeConfigs = scanViewportsForCompositeConfigs({
      viewportsConfigurations,
      stackProviders,
      studies,
      mainStudiesIds,
      enableHybridStacks: false,
      enablePriorStacks: true,
    });

    // Create composite providers if we found any
    if (compositeConfigs.length > 0) {
      setStackProviders((prevProviders) => {
        const newProviders = new Map(prevProviders);
        let hasChanges = false;

        for (const {
          config: compositeConfig,
          topProvider,
          topSeries,
          topStudy,
        } of compositeConfigs) {
          if (compositeConfig == null) continue;

          // Generate synthetic SMID
          const syntheticSmid = encodeCompositeStackSmid(
            compositeConfig.topStack.smid,
            compositeConfig.matches.map((m) => m.stack.smid)
          );

          // Skip if we already have this provider
          if (newProviders.has(syntheticSmid)) continue;

          const allMatchedProvidersExist = compositeConfig.matches.every(
            (match) => getBaseSingleStackProvider(newProviders, match.stack.smid) != null
          );
          if (!allMatchedProvidersExist) {
            continue;
          }

          const compositeProvider = createCompositeProvider({
            compositeStackSmid: syntheticSmid,
            compositeConfig,
            topProvider,
            topStudy,
            topSeries,
            providerMap: newProviders,
          });

          newProviders.set(syntheticSmid, compositeProvider);
          hasChanges = true;
        }

        if (hasChanges) {
          linkCompositeProviders(newProviders);
        }

        return hasChanges ? newProviders : prevProviders;
      });
    }
  }, [
    multiObjectStacksEnabled,
    enablePriorStacks,
    viewportsConfigurations,
    studies,
    mainStudiesIds,
    areViewportsReady,
    pixelDataSharedWorker,
    stackProviders,
    createCompositeProvider,
  ]);

  const getDisplayConfig = useRecoilCallback(
    ({ snapshot }) =>
      ({
        stackSmid,
        viewType,
        viewportId,
      }: {
        stackSmid: Nullable<string>;
        viewType: Nullable<ViewportTypeKeys>;
        viewportId: Nullable<string>;
      }) => {
        return snapshot
          .getLoadable(
            viewportDisplayConfigurationSelector({
              stackSmid,
              viewType,
              viewportId,
            })
          )
          .getValue();
      },
    []
  );

  useEffect(() => {
    if (viewportsConfigurations == null) {
      return;
    }

    Object.entries(viewportsConfigurations)
      .filter(
        ([id, vc]: [any, any]) =>
          vc?.stack != null &&
          stackProviders.has(vc.stack.smid) &&
          DRE_ALL_VIEWPORT_TYPES.includes(vc.viewType)
      )
      .forEach(([viewportId, { stack, viewType, series }]: [any, any]) => {
        const stackSmid = stack?.smid ?? ''; // we know stack exists
        const provider = stackProviders.get(stackSmid);

        const viewportDisplayConfigKey = provider?.getViewportDisplayConfigKey({
          viewType,
          viewportId,
        });

        if (viewportDisplayConfigKey == null) return null;

        // If the stack is a layered stack, we need to get the series smid from the active slice tags
        const slice = getDisplayConfig(viewportDisplayConfigKey)?.params2d?.imageNumber ?? 0;
        /**
         * Inject initial slice values into the providers so that broadcasted scroll updates
         * include stacks present on the other window. This is necessary because
         * `_updateVtkSliceForAllViewports` only broadcast states that already exist, and
         * states for the other window's viewports were only being made in response to
         * those other viewports scrolling and broadcasting new values to this window.
         * Now, we will always have the initial values pushed to those internal states so that
         * `_updateVtkSliceForAllViewports` always includes viewports in the other window.
         * However, we only should update the provider if it doesn't have state already;
         * The source we are pulling from updates more slowly than the providers.
         */
        provider?._initializeVtkSlice(viewType, viewportId, slice, false);
      });
  }, [stackProviders, viewportsConfigurations, getDisplayConfig]);

  const syncAllSnapshots = useCallback(() => {
    for (const targetProvider of stackProviders.values()) {
      targetProvider.snapshotManualSyncPosition();
    }
  }, [stackProviders]);

  const clearAllSnapshots = useCallback(() => {
    for (const targetProvider of stackProviders.values()) {
      targetProvider.clearManualSyncPosition();
    }
  }, [stackProviders]);

  const contextValue = useMemo(
    () => ({
      stackProviders,
      hybridMammographyEnabled,
      linkManager: linkManagerRef.current,
      synchronizationBroadcaster: synchronizationBroadcaster.current,
      syncAllSnapshots,
      pixelDataSharedWorker,
    }),
    [stackProviders, hybridMammographyEnabled, syncAllSnapshots, pixelDataSharedWorker]
  );

  useEffect(() => {
    const handlePageHide = (event: PageTransitionEvent) => {
      if (event.persisted) return;
      clearProviderMap();
    };
    // Dispose providers on page hide so the SharedWorker cancels in-flight loads
    // on refresh/close. React Router navigation is already covered by useCurrentCase.
    // Disposal also attempts to proactively clear image buffers
    // so that garbage collection more consistently triggers during case transition.
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  // Sync linked scrolling mode recoil state to the LinkManager
  useEffect(() => {
    const linkManager = linkManagerRef.current;
    switch (scrollLinkMode) {
      case ScrollLinkMode.Disabled:
        clearAllSnapshots();
        linkManager.disableLinkedScrolling();
        break;
      case ScrollLinkMode.Manual:
        linkManager.enableManualLinkedScrolling();
        syncAllSnapshots();
        break;
      case ScrollLinkMode.Automatic:
      default:
        // by clearing snapshots, linking functions can now tell
        // if we are in manual mode only when the snapshots exist
        clearAllSnapshots();
        linkManager.enableAutomaticLinkedScrolling();
    }
  }, [scrollLinkMode, syncAllSnapshots, clearAllSnapshots]);

  useEffect(() => {
    synchronizationBroadcaster.current.setMessageHandler(
      (data: { messages: Array<StackSyncBroadcastChannelMessage> }) => {
        data.messages.forEach((message) => {
          if (message.action === 'sliceChange') {
            const { viewType, viewportId, stackSmid, slice } = message.details;
            const imagingProvider = stackProviders.get(stackSmid);
            if (imagingProvider != null) {
              // Update the imaging provider for the stack that sent the message, if it exists.
              // This causes all other linked stack to be updated.
              imagingProvider.syncSliceFromBroadcast(viewType, viewportId, slice);
            }
          }
        });
      }
    );
  }, [stackProviders]);

  // VX-5244: keep each provider's loadPriority in line with the step being read. Hanged while any open window shows the
  // stack, otherwise its upcoming-step tier, otherwise unhung. A stack that changes tier mid-load is re-sent from this
  // window: the SharedWorker keeps the most urgent priority any connection asked for, so a stack only drops once every
  // window has dropped it -- which is why this runs in every window, unlike the viewer/0-only effect it replaces.
  //
  // A layout effect so it lands before ViewerMemoryManager's passive effects in the same commit, which reload any stack
  // that becomes Full at whatever loadPriority it holds.
  useLayoutEffect(() => {
    if (!isStepAwareLoadPriorityEnabled || viewportsConfigurations == null) return;
    // The extension's window list can come back without this window (VX-4764). The viewport configurations are
    // filtered by that list, so this window's own stacks would read as off-screen and be demoted under the reader.
    // Hold the last tiers until the list includes this window again, the same gate `areViewportsReady` applies.
    if (viewerId == null || !windows.includes(viewerId)) return;

    const providersOnScreen = new Set<BaseImagingProvider<Stack>>();
    for (const openWindowId of windows) {
      collectProvidersInWindowViewports({
        viewportsConfigurations,
        stackProviders,
        viewerId: openWindowId,
      }).forEach((provider) => providersOnScreen.add(provider));
    }
    const upcomingStepDistances = getUpcomingStepDistances({
      steps,
      activeStepIndex: stepIndex,
      openWindowIds: windows,
    });

    for (const provider of stackProviders.values()) {
      if (!isWorkerLoadedImagingProvider(provider)) continue;

      const priority = providersOnScreen.has(provider)
        ? LOADING_PRIORITIES.HANGED
        : getOffscreenPriority(provider.stack, upcomingStepDistances.get(provider.stack.smid));
      const previousPriority = provider.loadPriority;
      provider.loadPriority = priority;

      // A first assignment has nothing to correct: every load issued from here on reads loadPriority.
      if (previousPriority == null || previousPriority === priority) continue;
      // `error` can still hold a live request: failLoading() leaves it standing, and a deferred error keeps loading
      // into the cache under that status (reinitializeWorker treats both as in flight). The worker drops one it lacks.
      if (provider.status !== 'loading' && provider.status !== 'error') continue;

      provider.updateLoadPriority({
        priority,
        focus: Math.max(provider.getActiveSlice('TWO_D_DRE'), 0),
      });
    }
  }, [
    isStepAwareLoadPriorityEnabled,
    viewportsConfigurations,
    stackProviders,
    viewerId,
    windows,
    steps,
    stepIndex,
  ]);

  useEffect(() => {
    // VX-4775: this closure's viewportsConfigurations/stackProviders snapshot can go stale across the
    // await below (e.g. the user drags an unhanged stack into a viewport while hanged stacks are still
    // loading). `cancelled` lets a superseded pass bail out instead of restricting a stack that is, by
    // the time this resumes, already on screen — the sibling promotion effect only runs once per
    // config change and would have already run (as a no-op) for the render that made it hanged.
    let cancelled = false;

    async function loadUnhangedStacksNext() {
      if (viewportsConfigurations == null) {
        return;
      }

      // ignore viewports in other viewport windows, so that they get called
      // to load now (typically from the cache rather than over the network
      // simulatenously with the original window's load)
      const hangedStacks = Object.entries(viewportsConfigurations)
        // @ts-expect-error [EN-7967] - TS2769 - No overload matches this call.
        .filter(([viewportId]: [any]) => viewportIdToConfig(viewportId).windowId === viewerId)
        .map(([_, config]: [any, any]) => config?.stack?.smid);

      // wait for hanged stacks to finish loading
      await Promise.all(
        Array.from(stackProviders.values())
          .filter((provider) => hangedStacks.includes(provider.stack.smid))
          .map((provider) => provider.waitForLoadFinished())
      );
      if (cancelled) return;
      // VX-4775: `hangedStacks` holds viewport smids only, so a stack on screen behind a composite or
      // multi-layer viewport is absent from it. Such a stack still needs the unhung load below (it has
      // no pixels yet), but it must never be restricted to cache-only — it is already being displayed,
      // and nothing would promote it.
      const providersOnScreen = collectProvidersInWindowViewports({
        viewportsConfigurations,
        stackProviders,
        viewerId,
      });
      const hybridSwapProviders = getHybridMammographySwapProvidersInWindow({
        stackProviders,
        viewportsConfigurations,
        hybridMammographyEnabled,
        viewerId,
        stackProvidersInViewports: providersOnScreen,
      });

      for (const provider of stackProviders.values()) {
        if (
          !hangedStacks.includes(provider.stack.smid) &&
          provider.status === 'init' &&
          provider.hasPixels()
        ) {
          // VX-4775: prefetch this stack to the on-disk cache without transferring its frames into
          // renderer memory, when VIEWER_RESTRICT_INITIAL_LOADS elects to restrict it. Must be set
          // before load() so the `load-stack` request carries the mode.
          if (
            isWorkerLoadedImagingProvider(provider) &&
            !providersOnScreen.has(provider) &&
            shouldRestrictInitialLoad({
              restrictInitialLoads,
              studySmid: provider.stack.study.smid,
              mainStudySmids: mainStudiesIds,
            })
          ) {
            provider.restrictDeliveryUntilHanged();
          }
          // VX-5244: loadPriority is assigned only under VIEWER_STEP_AWARE_LOAD_PRIORITY, and is read at call time
          // rather than captured, since this pass resumes after an await that a step change can land in.
          provider.load({
            priority:
              (isWorkerLoadedImagingProvider(provider) ? provider.loadPriority : null) ??
              getUnhungPriority(provider.stack),
          });
          // The memory-manager child effect can run before this async parent effect applies the
          // cache-only restriction. Promote Full hybrid targets here as well, after load() has
          // initialized its pending frames, so they arrive resident for an immediate view switch.
          if (
            hybridSwapProviders.has(provider) &&
            provider.loadType === ImagingProviderLoadType.Full &&
            isWorkerLoadedImagingProvider(provider)
          ) {
            provider.promoteToDelivery();
          }
        }
      }
    }
    loadUnhangedStacksNext();
    return () => {
      cancelled = true;
    };
  }, [
    stackProviders,
    viewportsConfigurations,
    viewerId,
    restrictInitialLoads,
    mainStudiesIds,
    hybridMammographyEnabled,
  ]);

  // VX-4775: promote a restricted stack the moment it enters this window's layout. The memory manager
  // can't do this: an unhanged stack that fit the budget is already at loadType Full, so becoming
  // hanged is not a load-type transition and its reload branch never fires. Promotion is idempotent,
  // so re-running on unrelated configuration changes is free.
  useEffect(() => {
    // Recursion into composite constituents is required, not defensive: dragging a prior can produce a
    // composite viewport (buildPriorSeriesConfig), whose own provider owns no pixels. Matching on
    // viewport smids alone would promote nothing and the dropped prior would never render.
    for (const provider of collectProvidersInWindowViewports({
      viewportsConfigurations,
      stackProviders,
      viewerId,
    })) {
      if (isWorkerLoadedImagingProvider(provider)) provider.promoteToDelivery();
    }
  }, [stackProviders, viewportsConfigurations, viewerId]);

  // deprioritize stacks that stop being linked to the guide viewport
  useEffect(() => {
    if (
      viewerId !== '0' ||
      viewportsConfigurations == null ||
      guideViewportConfiguration == null ||
      guideViewportConfiguration.stack?.smid == null ||
      // @ts-expect-error [EN-7967] - TS2345 - Argument of type 'ViewportTypeKeys' is not assignable to parameter of type 'DreAll2DViewportTypes'.
      !DRE_ALL_2D_VIEWPORT_TYPES.includes(guideViewportConfiguration.viewType)
    ) {
      return;
    }

    const guideStackSmid = guideViewportConfiguration.stack.smid;
    const links = linkManagerRef.current.getLinksForView(
      guideStackSmid,
      // @ts-expect-error [incompatible-call] we check for DRE viewtypes above
      guideViewportConfiguration.viewType
    );

    stackProviders.forEach((provider) => {
      const firstHangedViewportSlice =
        provider.getFirstHangedViewportSlice(viewportsConfigurations);

      // exclude the guide provider, anything done loading,
      // anything not hanged, and anything that links to the guide
      if (
        provider.stack.smid === guideStackSmid ||
        provider.status !== 'loading' ||
        firstHangedViewportSlice == null ||
        links.some((l) => l.targetProvider.stack.smid === provider.stack.smid)
      ) {
        return;
      }

      provider.updateLoadPriority({
        priority: LOADING_PRIORITIES.HANGED,
        focus: firstHangedViewportSlice,
      });
    });
  }, [stackProviders, guideViewportConfiguration, viewerId, viewportsConfigurations]);

  // deprioritize stacks that stop being hanged
  useEffect(() => {
    if (
      // VX-5244: the step-aware layout effect above covers this, in every window
      isStepAwareLoadPriorityEnabled ||
      viewerId !== '0' ||
      viewportsConfigurations == null ||
      previousViewportsConfigurations == null
    ) {
      return;
    }

    stackProviders.forEach((provider) => {
      // loading, not found in current config, but WAS in previous config
      if (
        provider.status === 'loading' &&
        Object.values(viewportsConfigurations).every(
          (vc) => vc?.stack?.smid !== provider.stack.smid
        ) &&
        Object.values(previousViewportsConfigurations).some(
          (vc) => vc?.stack?.smid === provider.stack.smid
        )
      ) {
        provider.updateLoadPriority({
          priority: getUnhungPriority(provider.stack),
          focus: provider.getFirstHangedViewportSlice(previousViewportsConfigurations) ?? 0,
        });
      }
    });
  }, [
    isStepAwareLoadPriorityEnabled,
    viewerId,
    stackProviders,
    viewportsConfigurations,
    previousViewportsConfigurations,
  ]);

  return (
    <ContextProvider value={contextValue}>
      <Suspense>
        <ViewerMemoryManager
          stackProviders={stackProviders}
          viewportsConfigurations={viewportsConfigurations}
          areViewportsReady={areViewportsReady}
          hybridMammographyEnabled={hybridMammographyEnabled}
        />
      </Suspense>

      {children}
    </ContextProvider>
  );
};

export const ViewerContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const caseId = useCurrentCaseId();
  return (
    <ViewerRecoilContext key={caseId}>
      <InnerProvider>{children}</InnerProvider>
    </ViewerRecoilContext>
  );
};

export { ContextProvider, useViewerContext };

ViewerContextProvider.displayName = 'ViewerContextProvider';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(ViewerContextProvider);
